import React, { useState, useEffect } from 'react';
import MainLayout from '../components/layout/MainLayout';
import axios from 'axios';
import { toast } from 'react-toastify';

const Withdrawals = () => {
  const [loading, setLoading] = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [availableAmount, setAvailableAmount] = useState(0);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState({
    startDate: '',
    endDate: '',
    status: 'all'
  });

  useEffect(() => {
    fetchWithdrawals();
    fetchAvailableAmount();
  }, []);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get('/api/withdrawals', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: filter.startDate || undefined,
          endDate: filter.endDate || undefined,
          status: filter.status !== 'all' ? filter.status : undefined
        }
      });

      if (response.data) {
        setWithdrawals(response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar retiradas:', error);
      toast.error('Erro ao carregar extrato de retiradas. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableAmount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get('/api/withdrawals/available', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setAvailableAmount(response.data.amount || 0);
      }
    } catch (error) {
      console.error('Erro ao buscar valor disponível:', error);
    }
  };

  const handleWithdrawalRequest = async (e) => {
    e.preventDefault();
    
    if (!withdrawalAmount || parseFloat(withdrawalAmount) <= 0) {
      toast.error('Por favor, informe um valor válido para retirada.');
      return;
    }

    if (parseFloat(withdrawalAmount) > availableAmount) {
      toast.error('O valor solicitado é maior que o valor disponível para retirada.');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setProcessing(false);
        return;
      }

      await axios.post('/api/withdrawals', {
        amount: parseFloat(withdrawalAmount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Solicitação de retirada enviada com sucesso!');
      setWithdrawalAmount('');
      
      // Atualizar dados
      fetchWithdrawals();
      fetchAvailableAmount();
    } catch (error) {
      console.error('Erro ao solicitar retirada:', error);
      toast.error('Erro ao processar solicitação de retirada. Por favor, tente novamente.');
    } finally {
      setProcessing(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilter = (e) => {
    e.preventDefault();
    fetchWithdrawals();
  };

  const resetFilter = () => {
    setFilter({
      startDate: '',
      endDate: '',
      status: 'all'
    });
    
    // Buscar novamente sem filtros
    setTimeout(fetchWithdrawals, 0);
  };

  const formatCurrency = (value, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="badge bg-warning">Pendente</span>;
      case 'approved':
        return <span className="badge bg-success">Aprovado</span>;
      case 'rejected':
        return <span className="badge bg-danger">Rejeitado</span>;
      case 'completed':
        return <span className="badge bg-info">Concluído</span>;
      default:
        return <span className="badge bg-secondary">Desconhecido</span>;
    }
  };

  return (
    <MainLayout>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row align-items-center">
            <div className="col">
              <h2 className="page-title">Extrato de Retiradas</h2>
              <div className="text-muted mt-1">
                Gerencie suas solicitações de retirada e acompanhe o histórico
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="page-body">
        <div className="container-xl">
          <div className="row">
            <div className="col-md-8">
              {/* Filtros */}
              <div className="card mb-4">
                <div className="card-header">
                  <h3 className="card-title">Filtrar Retiradas</h3>
                </div>
                <div className="card-body">
                  <form onSubmit={applyFilter}>
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Data Inicial</label>
                        <input 
                          type="date" 
                          className="form-control" 
                          name="startDate"
                          value={filter.startDate}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Data Final</label>
                        <input 
                          type="date" 
                          className="form-control" 
                          name="endDate"
                          value={filter.endDate}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Status</label>
                        <select 
                          className="form-select" 
                          name="status"
                          value={filter.status}
                          onChange={handleFilterChange}
                        >
                          <option value="all">Todos</option>
                          <option value="pending">Pendente</option>
                          <option value="approved">Aprovado</option>
                          <option value="rejected">Rejeitado</option>
                          <option value="completed">Concluído</option>
                        </select>
                      </div>
                    </div>
                    <div className="d-flex justify-content-end">
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary me-2"
                        onClick={resetFilter}
                      >
                        Limpar Filtros
                      </button>
                      <button type="submit" className="btn btn-primary">
                        Aplicar Filtros
                      </button>
                    </div>
                  </form>
                </div>
              </div>
              
              {/* Tabela de retiradas */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Histórico de Retiradas</h3>
                </div>
                <div className="card-body p-0">
                  {loading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <div className="mt-3">Carregando histórico de retiradas...</div>
                    </div>
                  ) : withdrawals.length === 0 ? (
                    <div className="empty">
                      <div className="empty-img">
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-cash-off" width="50" height="50" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                          <path d="M13 9h6a2 2 0 0 1 2 2v6m-2 2h-10a2 2 0 0 1 -2 -2v-6a2 2 0 0 1 2 -2"></path>
                          <path d="M12.582 12.59a2 2 0 0 0 2.83 2.826"></path>
                          <path d="M17 9v-2a2 2 0 0 0 -2 -2h-6m-4 0a2 2 0 0 0 -2 2v6a2 2 0 0 0 2 2h2"></path>
                          <path d="M3 3l18 18"></path>
                        </svg>
                      </div>
                      <p className="empty-title">Nenhuma retirada encontrada</p>
                      <p className="empty-subtitle text-muted">
                        Você ainda não possui retiradas registradas ou nenhuma retirada corresponde aos filtros aplicados.
                      </p>
                      <div className="empty-action">
                        <button className="btn btn-primary" onClick={resetFilter}>
                          Limpar Filtros
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-vcenter card-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Data</th>
                            <th>Valor</th>
                            <th>Status</th>
                            <th>Atualizado em</th>
                          </tr>
                        </thead>
                        <tbody>
                          {withdrawals.map(withdrawal => (
                            <tr key={withdrawal.id}>
                              <td>{withdrawal.id}</td>
                              <td>{formatDate(withdrawal.created_at)}</td>
                              <td>{formatCurrency(withdrawal.amount)}</td>
                              <td>{getStatusBadge(withdrawal.status)}</td>
                              <td>{formatDate(withdrawal.updated_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="col-md-4">
              {/* Valor disponível */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="subheader">Valor Disponível para Retirada</div>
                  </div>
                  <div className="h1 mb-3">{formatCurrency(availableAmount)}</div>
                  <div className="d-flex mb-2">
                    <div>Atualizado em</div>
                    <div className="ms-auto">{formatDate(new Date())}</div>
                  </div>
                  <div className="progress progress-sm">
                    <div className="progress-bar bg-primary" style={{ width: '100%' }} role="progressbar" aria-valuenow="100" aria-valuemin="0" aria-valuemax="100">
                      <span className="visually-hidden">100% Disponível</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Formulário de retirada */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Solicitar Retirada</h3>
                </div>
                <div className="card-body">
                  <form onSubmit={handleWithdrawalRequest}>
                    <div className="mb-3">
                      <label className="form-label">Valor a Retirar</label>
                      <div className="input-group">
                        <span className="input-group-text">R$</span>
                        <input 
                          type="number" 
                          className="form-control" 
                          placeholder="0,00" 
                          step="0.01"
                          min="0.01"
                          max={availableAmount}
                          value={withdrawalAmount}
                          onChange={(e) => setWithdrawalAmount(e.target.value)}
                          required
                        />
                      </div>
                      <small className="form-hint">
                        Valor máximo: {formatCurrency(availableAmount)}
                      </small>
                    </div>
                    
                    <div className="alert alert-info mb-3">
                      <h4 className="alert-title">Informações Importantes</h4>
                      <ul className="mb-0">
                        <li>Solicitações feitas até às 14h serão processadas no mesmo dia.</li>
                        <li>O valor mínimo para retirada é de R$ 100,00.</li>
                        <li>O prazo para processamento é de até 2 dias úteis.</li>
                      </ul>
                    </div>
                    
                    <button 
                      type="submit" 
                      className="btn btn-primary w-100" 
                      disabled={processing || !withdrawalAmount || parseFloat(withdrawalAmount) <= 0 || parseFloat(withdrawalAmount) > availableAmount}
                    >
                      {processing ? 'Processando...' : 'Solicitar Retirada'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Withdrawals; 